// Import helpers
import { convertDateToDayMonthYear } from 'helpers/time';

// Import types
import { PacketStatus, type CalculatedPriceProduct, type UserSubscription, type CurrentProduct } from 'types/api/packetsSubscription';
import type { IncludedPacketsProduct } from 'types/api/products/packets';
import type { PacketWithPriceAndChannels } from 'types/views/configurator';
import type { UserSubscriptionParsed } from 'types/views/subscription';
import { CalculatePriceAction } from 'types/api/packetsSubscription/calculatePrice';
import { PaymentStatus, type LastPayments, type LastPaymentsRedirectUrl } from 'types/api/payments';
import { PacketSubtype } from 'types/api/products/product';

type Product = CurrentProduct | IncludedPacketsProduct;

export const isPacketsEqual =
	(uuid: string) =>
	({ product_uuid }: Product) =>
		product_uuid === uuid;

export const checkIsIncluded = (uuid: string, array?: Product[] | null) => array?.some(isPacketsEqual(uuid)) ?? false;

export const getCalculatedPrice = (uuid: string, array?: CalculatedPriceProduct[]) => array?.find((product) => product.uuid === uuid);

export const findPacket = (array: PacketWithPriceAndChannels[], uuid: string | undefined) => array?.find((packet) => packet.uuid === uuid);

export const formatPacketDate = (date: string | Date) => convertDateToDayMonthYear(date);

export const getActionType = (subscription: UserSubscriptionParsed | null, price?: CalculatedPriceProduct): CalculatePriceAction => {
	if (!subscription) return CalculatePriceAction.New;
	const isDowngrade = price ? price.current_price < subscription.main.price : false;
	return isDowngrade ? CalculatePriceAction.Downgrade : CalculatePriceAction.Upgrade;
};

const getDowngradingPacket = (pendingPacket: CurrentProduct | null, currentPacket: CurrentProduct, isCurrentPacketCanceled: boolean) => {
	if (!pendingPacket) return null;

	const isDowngradingPacketExist = isCurrentPacketCanceled && pendingPacket.price < currentPacket.price;

	return isDowngradingPacketExist ? pendingPacket : null;
};

export const transformSubscription = (data: UserSubscription | null): UserSubscriptionParsed | null => {
	if (!data) return null;

	const main = Object.assign({ subtype: PacketSubtype.Main }, data.current_basic_product);
	const premium = data.current_additional_products.map((packet) => Object.assign({ subtype: PacketSubtype.Premium }, packet));

	const since = formatPacketDate(data.billing_period_start_on);
	const till = formatPacketDate(data.billing_period_end_on);

	const expires_on = formatPacketDate(main.expires_on);
	const isCanceled = main.status === PacketStatus.Canceled;
	const isActive = main.status === PacketStatus.Active;

	const downgrading = getDowngradingPacket(data.pending_basic_product, main, isCanceled);

	return { main, premium, downgrading, since, till, expires_on, isCanceled, isActive };
};

export const transformLastPayments = (data: LastPayments): LastPaymentsRedirectUrl | null => {
	const payment = data.packet_redirect_url;

	if (!payment) return null;

	switch (payment.status) {
		case PaymentStatus.Pending:
			return payment.redirect_url || payment.payment_id ? payment : null;

		case PaymentStatus.Failed:
			return payment.redirect_url ? payment : null;

		default:
			return null;
	}
};
